import type {
    CommerceProduct,
    CommerceProductSort,
    CommerceProductSummary,
    SearchCommerceProductsRequest,
} from "@teqtank/commerce-contracts";
import { MockCommerceTransport } from "@teqtank/commerce-transport";
import { getCommerceProductCategoriesOperation } from "../operations/get-commerce-product-categories-operation.js";
import { getCommerceProductCategoryOperation } from "../operations/get-commerce-product-category-operation.js";
import { getCommerceProductOperation } from "../operations/get-commerce-product-operation.js";
import { searchCommerceProductsOperation } from "../operations/search-commerce-products-operation.js";
import { mockProductCategories, mockProducts } from "./mock-product-catalogue.js";

export function registerDefaultProductOperations(transport: MockCommerceTransport): void {
    transport.register(searchCommerceProductsOperation, (request) => searchProducts(request));

    transport.register(getCommerceProductOperation, ({ identifier, identity }) => ({
        product:
            mockProducts.find((product) => {
                switch (identity) {
                    case "slug":
                        return product.slug === identifier;
                    case "sku":
                        return product.displaySku === identifier;
                    case "id":
                    default:
                        return product.id === identifier;
                }
            }) ?? null,
    }));

    transport.register(getCommerceProductCategoryOperation, ({ categoryId }) => ({
        category: mockProductCategories.find((category) => category.id === categoryId) ?? null,
    }));

    transport.register(getCommerceProductCategoriesOperation, ({ parentId }) => ({
        categories: mockProductCategories
            .filter((category) => (parentId === undefined ? true : category.parentId === parentId))
            .sort((left, right) => left.sortOrder - right.sortOrder),
    }));
}

function searchProducts(request: SearchCommerceProductsRequest) {
    const page = request.page ?? 1;
    const pageSize = request.pageSize ?? 24;

    let products = [...mockProducts];

    if (request.query !== undefined) {
        const query = request.query.toLowerCase();

        products = products.filter((product) =>
            [
                product.name,
                product.displaySku ?? "",
                product.shortDescription,
                product.description,
                ...product.tags,
            ].some((value) => value.toLowerCase().includes(query)),
        );
    }

    if (request.categoryId !== undefined) {
        products = products.filter((product) =>
            product.categoryIds.includes(request.categoryId as string),
        );
    }

    if (request.tags !== undefined) {
        products = products.filter(
            (product) => request.tags?.every((tag) => product.tags.includes(tag)) === true,
        );
    }

    if (request.status !== undefined) {
        products = products.filter((product) => product.status === request.status);
    }

    if (request.recurringAvailable !== undefined) {
        products = products.filter(
            (product) =>
                product.representativeOffers.some((offer) => offer.purchaseType === "recurring") ===
                request.recurringAvailable,
        );
    }

    products = sortProducts(products, request.sort ?? "default");

    const totalItems = products.length;
    const totalPages = totalItems === 0 ? 0 : Math.ceil(totalItems / pageSize);

    const startIndex = (page - 1) * pageSize;

    const items = products.slice(startIndex, startIndex + pageSize).map(toProductSummary);

    return {
        items,
        pagination: {
            page,
            pageSize,
            totalItems,
            totalPages,
            hasPreviousPage: page > 1,
            hasNextPage: page < totalPages,
        },
    };
}

function sortProducts(
    products: readonly CommerceProduct[],
    sort: CommerceProductSort,
): CommerceProduct[] {
    const sorted = [...products];

    switch (sort) {
        case "name-ascending":
            return sorted.sort((left, right) => left.name.localeCompare(right.name));

        case "name-descending":
            return sorted.sort((left, right) => right.name.localeCompare(left.name));

        case "price-ascending":
            return sorted.sort(
                (left, right) => representativeAmount(left) - representativeAmount(right),
            );

        case "price-descending":
            return sorted.sort(
                (left, right) => representativeAmount(right) - representativeAmount(left),
            );

        case "newest":
            return sorted.reverse();

        case "default":
        default:
            return sorted;
    }
}

function toProductSummary(product: CommerceProduct): CommerceProductSummary {
    return {
        id: product.id,
        ...(product.displaySku === undefined ? {} : { displaySku: product.displaySku }),
        slug: product.slug,
        name: product.name,
        shortDescription: product.shortDescription,
        status: product.status,
        primaryImage: product.primaryImage,
        representativeOffers: product.representativeOffers,
        categoryIds: product.categoryIds,
        tags: product.tags,
    };
}

function representativeAmount(product: CommerceProduct): number {
    return (
        product.representativeOffers.find((offer) => offer.purchaseType === "one-time")?.price
            .current.amount ??
        product.representativeOffers[0]?.price.current.amount ??
        Number.POSITIVE_INFINITY
    );
}
